import {
  CUSTOM_ACCESS_RESOURCES_PROHIBITED_ROLES,
  CustomAccessResorces,
} from 'constants/customAccessResources';
import { OperationAccess } from 'constants/operationAccesses';
import { Resource, Resources } from 'constants/resources';
import { PAYMENT_ROLES, Role } from 'constants/roles';
import { FINANCE_ROLES } from 'constants/roles';

import { useContext, useMemo } from 'react';

import { APIContext } from 'context/apiContext';
import { NotFound } from 'pages/NotFound';
import { Loading, ResourceProps } from 'react-admin';
import { Permission, Permissions } from 'types/embedded/permission';

function getResourceFromPath(path: string): string {
  //обрезаем /admin/ из названия ресурса
  return path.slice(path.indexOf('/', 1) + 1);
}

function getCurrentPath(permissions: Permissions, name: string): string | undefined {
  return Object.keys(permissions).find((path) => getResourceFromPath(path) === name);
}

function checkUserHasOneOfRoles(userRoles: Role[], allowedRoles: Role[]) {
  return userRoles?.some((role) => allowedRoles.includes(role));
}

function getAdminPath(resource: Resource) {
  return `/admin/${resource}` as const;
}

type ReturnUsePermissions = {
  getResourceFromPath: (path: string) => string;
  getResourcePropsByPermissions: (props: ResourceProps) => ResourceProps;
  hasResourceAccess: (resource: Resource) => boolean;
  hasResourcePermissions: (resource: Resource, permissions: Permission[]) => boolean;
  hasOperationAccess: (operationAccess: OperationAccess) => boolean;
  hasAnyRole(allowedRoles: Role[]): boolean;
  roles: Role[];
  permissions: Permissions;
  isHaveRole: (roles: Role[]) => boolean;
};

export function usePermissions(): ReturnUsePermissions {
  const { user } = useContext(APIContext);

  const userRoles = useMemo(() => user.roles, [user]);

  const isHaveRole = (roles: Role[]) => checkUserHasOneOfRoles(userRoles, roles);

  const orderAdjustmentRolePermission = {
    [getAdminPath(Resources.OrderAdjustmentsReport)]: [Permission.Full],
  };

  const userPermissions = useMemo<Permissions>(() => {
    if (!checkUserHasOneOfRoles(userRoles, [Role.Admin, Role.Tech])) {
      delete user.permissions?.[getAdminPath(Resources.Users)];
    }

    if (!user.permissions?.[getAdminPath(Resources.BankOperationsPendingStatuses)]) {
      delete user.permissions?.[getAdminPath(Resources.RequestBankOperations)];
    }

    return {
      ...user.permissions,
      ...(checkUserHasOneOfRoles(userRoles, [...FINANCE_ROLES, Role.Admin, Role.Tech]) && {
        [getAdminPath(Resources.OrdersReport)]: [Permission.Full, Permission.Update],
      }),
      ...(checkUserHasOneOfRoles(userRoles, [...FINANCE_ROLES, Role.Tech, Role.Admin]) && {
        [getAdminPath(Resources.CumulativeReport)]: [Permission.Full],
      }),
      ...(checkUserHasOneOfRoles(userRoles, [...PAYMENT_ROLES, Role.Tech, Role.Admin]) && {
        [getAdminPath(Resources.OrderConversionChart)]: [Permission.Full],
      }),
      ...(checkUserHasOneOfRoles(userRoles, [Role.Tech, Role.Admin]) && {
        [getAdminPath(Resources.OrderManualCreate)]: [Permission.Full],
      }),
      ...(checkUserHasOneOfRoles(userRoles, [
        ...FINANCE_ROLES,
        ...PAYMENT_ROLES,
        Role.Tech,
        Role.Admin,
        Role.Risk,
      ]) && orderAdjustmentRolePermission),
      ...(checkUserHasOneOfRoles(userRoles, [...FINANCE_ROLES, Role.Tech, Role.Admin]) && {
        [getAdminPath(Resources.RefundChargebackReport)]: [Permission.Full],
      }),
      ...(checkUserHasOneOfRoles(userRoles, [...PAYMENT_ROLES, Role.Tech, Role.Admin]) && {
        [getAdminPath(Resources.Dashboard)]: [Permission.Read],
      }),
      ...(checkUserHasOneOfRoles(userRoles, [...FINANCE_ROLES, Role.Admin]) && {
        [getAdminPath(Resources.Reconciliation)]: [Permission.Full],
      }),
    };
  }, [user]);

  function hasAnyRole(allowedRoles: Role[]): boolean {
    return allowedRoles.some((role) => userRoles.includes(role));
  }

  function hasOperationAccess(operationAccess: OperationAccess): boolean {
    return !!user.userOperationAccess?.includes(operationAccess);
  }

  function hasResourceAccess(resource: Resource): boolean {
    const allAvailableResources = Object.keys(userPermissions).reduce((resources, currentPath) => {
      return [...resources, getResourceFromPath(currentPath)];
    }, [] as string[]);

    return allAvailableResources.includes(resource);
  }

  function hasResourcePermissions(resource: Resource, permissions: Permission[]): boolean {
    const currentPath = getCurrentPath(userPermissions, resource);

    if (!currentPath) {
      return false;
    }

    // делаем проверку на фронте, потому что на бэке нету урла order_adjustments_report
    const isOrderAdjustment = currentPath === getAdminPath(Resources.OrderAdjustments);
    const prohibitedRoles = [Role.FinanceLead, Role.Finance];

    if (isOrderAdjustment && checkUserHasOneOfRoles(userRoles, prohibitedRoles)) {
      return false;
    }

    const currentPathPermissions = userPermissions[currentPath];
    return permissions.some((permission) => currentPathPermissions.includes(permission));
  }

  function getResourcePropsByPermissions(props: ResourceProps): ResourceProps {
    const currentPath = getCurrentPath(userPermissions, props.name);

    if (!currentPath) {
      return {
        name: props.name,
        create: Loading,
        edit: Loading,
        list: Loading,
        show: Loading,
      };
    }

    const currentPathPermissions = userPermissions[currentPath];
    const currentResource = getResourceFromPath(currentPath);

    const isProhibitedResource = checkUserHasOneOfRoles(
      userRoles,
      CUSTOM_ACCESS_RESOURCES_PROHIBITED_ROLES[currentResource as CustomAccessResorces] ?? [],
    );

    const getListPermission = () => {
      const hasNoListPermission =
        !currentPathPermissions.includes(Permission.Read) &&
        !currentPathPermissions.includes(Permission.Full);
      if (hasNoListPermission || isProhibitedResource) {
        return NotFound;
      }

      return props.list;
    };

    if (currentPathPermissions.includes(Permission.Full)) {
      return {
        ...props,
        list: getListPermission(),
      };
    }

    const isShowAccess =
      (currentPathPermissions.includes(Permission.Read) ||
        currentPathPermissions.includes(Permission.Partial)) &&
      !isProhibitedResource;

    return {
      ...props,
      create: currentPathPermissions.includes(Permission.Create) ? props.create : NotFound,
      edit: currentPathPermissions.includes(Permission.Update) ? props.edit : NotFound,
      show: isShowAccess ? props.show : NotFound,
      list: getListPermission(),
    };
  }

  return {
    getResourceFromPath,
    getResourcePropsByPermissions,
    hasResourceAccess,
    hasResourcePermissions,
    hasOperationAccess,
    hasAnyRole,
    roles: userRoles,
    permissions: userPermissions,
    isHaveRole,
  };
}
